import {
  Alert,
  AlertIcon,
  Box,
  Divider,
  Heading,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApi from "../../../shared/hooks/useApi";
import { SavedReportId, ScheduledSavedReportId } from "../../../shared/schema/schema";
import ScheduleReportForm from "./ScheduleReportForm";

type Props = {
  reportType: "saved" | "standard";
  reportId: number;
};

export default function ScheduledReportsSection({ reportId, reportType }: Props) {
  const { mutations, queries } = useApi();
  const queryClient = useQueryClient();

  const savedOptions = queries.scheduledReports.scheduledHistory(reportId as SavedReportId);
  const standardOptions = queries.scheduledReports.standardScheduledHistory(reportId);

  const savedSchedules = useQuery({ ...savedOptions, enabled: reportType === "saved" });
  const standardSchedules = useQuery({ ...standardOptions, enabled: reportType === "standard" });

  const schedules = reportType === "saved" ? savedSchedules : standardSchedules;
  const activeQueryKey = reportType === "saved" ? savedOptions.queryKey : standardOptions.queryKey;

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: activeQueryKey,
    });

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading mb={3} size="sm">
          New schedule
        </Heading>
        <ScheduleReportForm reportId={reportId} reportType={reportType} onCreated={invalidate} />
      </Box>

      <Divider />

      <Box>
        <Heading mb={3} size="sm">
          Existing schedules
        </Heading>
        {schedules.status === "pending" && <Spinner />}
        {schedules.status === "error" && (
          <Alert status="error">
            <AlertIcon />
            Failed to load schedules
          </Alert>
        )}
        {schedules.status === "success" &&
          (schedules.data.length === 0 ? (
            <Text color="gray.500">No schedules yet.</Text>
          ) : (
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Frequency</Th>
                  <Th>Time</Th>
                  <Th>Recipients</Th>
                  <Th>Active</Th>
                </Tr>
              </Thead>
              <Tbody>
                {schedules.data.map((row) => (
                  <Tr key={row.scheduledReport.id}>
                    <Td>{row.scheduledReport.periodType}</Td>
                    <Td>{row.scheduledReport.timeInDay}</Td>
                    <Td>{row.scheduledReport.recipientEmails.join(", ")}</Td>
                    <Td>
                      <ScheduleActiveToggle
                        active={row.scheduledReport.active}
                        id={row.scheduledReport.id}
                        mutations={mutations}
                        onToggled={invalidate}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ))}
      </Box>
    </VStack>
  );
}

function ScheduleActiveToggle({
  active,
  id,
  mutations,
  onToggled,
}: {
  active: boolean;
  id: ScheduledSavedReportId;
  mutations: ReturnType<typeof useApi>["mutations"];
  onToggled: () => void;
}) {
  const toggle = useMutation({
    ...mutations.scheduledReports.toggleSchedule(id),
    onSuccess: onToggled,
  });

  return (
    <Switch
      isChecked={active}
      isDisabled={toggle.isPending}
      onChange={() => toggle.mutate({ type: active ? "DEACTIVATE" : "ACTIVATE" })}
    />
  );
}
